import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Flex,
  Heading,
  Button,
  Tooltip,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  white,
  textSecondary,
  textPrimary,
  primaryAlt,
} from "../../../utils/colors";
import { motion } from "framer-motion";
import FormControlCountry from "../../input/FormControlCountry";
import {
  updateTenantOperatingCountryAction,
  seedDatabaseUpdateOnboardingStatusAction,
} from "../../../actions/tenantActions";
import { TENANT_UPDATE_OPERATINGCOUNTRY_RESET } from "../../../constants/tenantConstants";
import CountriesData from "../../../data/countries.json";
import { useNavigate } from "react-router-dom";

const MotionFlex = motion(Flex);

// Function to fetch country based on IP
const fetchUserCountry = async () => {
  try {
    const response = await fetch(
      `https://ipinfo.io/json?token=${process.env.REACT_APP_IPINFO_API_KEY}`
    );
    if (!response.ok) throw new Error("Failed to fetch country");

    const data = await response.json();
    // console.log("data country", data.country);
    return data.country || "US"; // Default to "US" if detection fails
  } catch (error) {
    console.error("Error fetching country:", error);
    return "US"; // Fallback to US
  }
};

const OnboardingStepThree = ({
  operatingCountry,
  setOperatingCountry,
  setStep,
  setStepSuccess,
  tenant,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  // STATE
  const [countryCode, setCountryCode] = useState(""); // Store country code
  const [isFetchingCountry, setIsFetchingCountry] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // REDUX
  const tenantUpdateOperatingCountry = useSelector(
    (state) => state.tenantUpdateOperatingCountry
  );
  const {
    loading: loadingUpdateOperatingCountry,
    error: errorUpdateOperatingCountry,
    success: successUpdateOperatingCountry,
  } = tenantUpdateOperatingCountry;

  const tenantSeedDatabaseUpdateOnboardingStatus = useSelector(
    (state) => state.tenantSeedDatabaseUpdateOnboardingStatus
  );
  const {
    loading: loadingSeedDatabaseUpdateOnboardingStatus,
    success: successSeedDatabaseUpdateOnboardingStatus,
    error: errorSeedDatabaseUpdateOnboardingStatus,
    slug,
  } = tenantSeedDatabaseUpdateOnboardingStatus;

  // ERRORS
  const isOperatingCountryError =
    !operatingCountry ||
    operatingCountry === undefined ||
    operatingCountry === "Select Country";

  // REDUX
  const dispatch = useDispatch();

  // HANDLERS
  const handleSubmit = (event) => {
    event.preventDefault();

    if (isOperatingCountryError || isSubmitting) return;

    setIsSubmitting(true); // ⛔ prevent further submissions

    dispatch(updateTenantOperatingCountryAction(operatingCountry, tenant.slug));
  };

  // Detect country and set in input
  useEffect(() => {
    const detectCountry = async () => {
      const detectedCountryCode = await fetchUserCountry();

      // console.log("detectedCountryCode", detectedCountryCode);

      // Find matching country name from JSON
      const matchedCountry = CountriesData.find(
        (country) => country.cca2 === detectedCountryCode
      );

      // console.log("matchedCountry", matchedCountry);

      if (matchedCountry) {
        setOperatingCountry(matchedCountry.cca2);
        setCountryCode(matchedCountry.cca2);
      } else {
        setOperatingCountry("United States"); // Default fallback
        setCountryCode("US");
      }

      setIsFetchingCountry(false);
    };

    if (!operatingCountry) {
      detectCountry();
    }
  }, [operatingCountry]);

  // ✅ Run when country update is successful
  useEffect(() => {
    if (successUpdateOperatingCountry) {
      setIsSubmitting(false); // ✅ re-enable submit
      dispatch({
        type: TENANT_UPDATE_OPERATINGCOUNTRY_RESET,
      });

      // ✅ Then, seed the database
      dispatch(
        seedDatabaseUpdateOnboardingStatusAction("completed", tenant.slug)
      );
    } else if (errorUpdateOperatingCountry) {
      setIsSubmitting(false); // ✅ re-enable submit
      toast({
        title: t("toasts.onboardingUpdateOperatingCountryError"),
        status: "error",
        duration: 9000,
        isClosable: true,
      });

      dispatch({ type: TENANT_UPDATE_OPERATINGCOUNTRY_RESET });
    }
  }, [successUpdateOperatingCountry, errorUpdateOperatingCountry]);

  // ✅ Run when seeding is successful
  useEffect(() => {
    if (successSeedDatabaseUpdateOnboardingStatus) {
      setStep(3);
      setStepSuccess(true);

      // dispatch({
      //   type: TENANT_SEED_DATABASE_UPDATE_ONBOARDING_STATUS_RESET,
      // });
    } else if (errorSeedDatabaseUpdateOnboardingStatus) {
      toast({
        title: t("toasts.updateTenantOnboardingStatusError"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      // dispatch({ type: TENANT_SEED_DATABASE_UPDATE_ONBOARDING_STATUS_RESET });
    }
  }, [
    successSeedDatabaseUpdateOnboardingStatus,
    errorSeedDatabaseUpdateOnboardingStatus,
  ]);

  useEffect(() => {
    if (slug) {
      navigate(`/${slug}/dashboard`);
    }
  }, [slug]);

  // LOGS
  // console.log("operatingCountry", operatingCountry);
  // console.log("isOperatingCountryError", isOperatingCountryError);

  // console.log("countryCode", countryCode);

  // console.log("slug", slug);

  return (
    <>
      <form onSubmit={handleSubmit}>
        <MotionFlex
          key="step2"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Flex
            direction="column"
            align={["flex-start", "center"]}
            justify="center"
            p={["4rem 1.5rem", "4rem"]}
            maxW={["100%", "800px"]}
          >
            <Heading
              fontSize={["2.5rem", "3rem"]}
              textAlign={["left", "center"]}
              maxW="100%"
            >
              {/* Where Is Your Clinic Located? */}
              {t("onboarding.questionThree")}
            </Heading>

            <Text
              fontSize="1rem"
              fontWeight="500"
              textAlign={["left", "center"]}
              color={textSecondary}
              maxW={["100%", "70%"]}
              mt={4}
            >
              {/* This helps us set up region-specific settings, regulations, and
              compliance. You can update this later. */}
              {t("onboarding.questionThreeText")}
            </Text>

            <Flex
              gap={[2, 4]}
              align={["flex-start", "center"]}
              justify="center"
              p="2rem 0 2rem"
              w="100%"
              direction="column"
            >
              <Text
                fontSize="md"
                fontWeight="500"
                //   color={textSecondary}
                color={textPrimary}
                //   textTransform="uppercase"
              >
                {t("onboarding.enterAddressOrChooseCountry")}
              </Text>
              <Flex
                align="center"
                justify={["flex-start", "center"]}
                w="100%"
                maxW={["100%", "400px"]}
                m="0 auto"
              >
                <FormControlCountry
                  isSearchable={true}
                  maxMenuHeight={240}
                  autoFocus
                  isBig
                  noLabel
                  isRounded
                  value={operatingCountry}
                  onChange={(selectedOption) =>
                    setOperatingCountry(selectedOption.value)
                  }
                />
              </Flex>
            </Flex>

            <Flex align="center" justify="center" w="100%" gap={4}>
              <Tooltip
                label={t("onboarding.operatingCountryError")}
                placement="top"
                p="0.25rem 1rem"
                borderRadius="1rem"
                isDisabled={!isOperatingCountryError}
              >
                <Button
                  h="3.5rem"
                  size="lg"
                  borderRadius="10rem"
                  onClick={handleSubmit}
                  isLoading={
                    loadingUpdateOperatingCountry ||
                    loadingSeedDatabaseUpdateOnboardingStatus ||
                    isSubmitting
                  }
                  bg={textPrimary}
                  px="3rem"
                  minW={["100%", "10rem"]}
                  color={white}
                  transition="all 0.3s ease"
                  _hover={{
                    bg: primaryAlt,
                    color: white,
                  }}
                  isDisabled={isOperatingCountryError}
                >
                  {t("onboarding.continue")}
                </Button>
              </Tooltip>
            </Flex>
          </Flex>
        </MotionFlex>
      </form>
    </>
  );
};

export default OnboardingStepThree;
